import { useReducer } from 'react'
import { getErrorMessage } from '../lib/utils/get-error-message'

type Props<TData, TResult> = {
  mutation: (data: TData) => Promise<TResult>
  onSuccess?: (result: TResult, data: TData) => void
  onError?: (error: string) => void
}

type MutationState = {
  isPending: boolean
  isError: boolean
  isSuccess: boolean
  error?: string
}

type Mutation<TData> = MutationState & {
  mutate: (data: TData) => Promise<void>
}

type Action =
  | { type: 'PENDING' }
  | { type: 'SUCCESS' }
  | { type: 'ERROR'; error: string }
  | { type: 'RESET' }

const initialState: MutationState = {
  isPending: false,
  isError: false,
  isSuccess: false,
}

function reducer(state: MutationState, action: Action): MutationState {
  switch (action.type) {
    case 'PENDING':
      return {
        isPending: true,
        isError: false,
        isSuccess: false,
        error: undefined,
      }
    case 'SUCCESS':
      return {
        isPending: false,
        isError: false,
        isSuccess: true,
        error: undefined,
      }
    case 'ERROR':
      return {
        isPending: false,
        isError: true,
        isSuccess: false,
        error: action.error,
      }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

export function useMutation<TData = unknown, TResult = unknown>({
  mutation,
  onSuccess,
  onError,
}: Props<TData, TResult>): Mutation<TData> {
  const [state, dispatch] = useReducer(reducer, initialState)

  const mutate = async (data: TData) => {
    dispatch({ type: 'PENDING' })
    try {
      const result = await mutation(data)
      dispatch({ type: 'SUCCESS' })
      onSuccess?.(result, data)
    } catch (err) {
      dispatch({ type: 'ERROR', error: getErrorMessage(err) })
      onError?.(getErrorMessage(err))
    }
  }

  return {
    mutate,
    ...state,
    error: state.error ?? '',
  }
}
